import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  Crown,
  Bus,
  Users,
  Baby,
  AlertTriangle,
  TriangleAlert,
  BadgeDollarSign,
  Merge,
  Radio,
  CalendarPlus,
  Layers,
} from "lucide-react";
import SalidaCard from "@/components/agenda/SalidaCard";
import SemanaView from "@/components/agenda/SemanaView";
import SalidaDetailSheet from "@/components/agenda/SalidaDetailSheet";
import GrupoDetailSheet from "@/components/agenda/GrupoDetailSheet";
import UnificarDialog from "@/components/agenda/UnificarDialog";
import CombinarHorarioDialog from "@/components/agenda/CombinarHorarioDialog";
import ProgramarSalidaDialog from "@/components/agenda/ProgramarSalidaDialog";
import { useAgendaSocket } from "@/hooks/useAgendaSocket";
import { todayStr, addDaysStr, startOfWeekStr, formatFechaLarga, dateToStr, strToDate } from "@/lib/agenda";

// Tiempo real: se agrupan los avisos que llegan seguidos (una sync de Bókun emite ráfagas).
const REFRESH_DEBOUNCE_MS = 1200;

function StatChip({ icon: Icon, label, value, tone = "slate" }) {
  const tones = {
    slate: "text-slate-700",
    amber: "text-amber-600",
    red: "text-red-600",
    rose: "text-rose-600",
  };
  return (
    <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border border-slate-200 bg-white">
      <Icon className={`h-4 w-4 ${tones[tone]}`} />
      <div className="leading-none">
        <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
        <p className={`text-lg font-bold tabular-nums ${tones[tone]}`}>{value}</p>
      </div>
    </div>
  );
}

export default function AgendaPage() {
  const [vista, setVista] = useState("dia");
  const [fecha, setFecha] = useState(todayStr());
  const [dia, setDia] = useState(null);
  const [semana, setSemana] = useState(null);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selSalidaId, setSelSalidaId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selGrupoId, setSelGrupoId] = useState(null);
  const [grupoDetailOpen, setGrupoDetailOpen] = useState(false);
  const [unifOpen, setUnifOpen] = useState(false);
  const [combinarOpen, setCombinarOpen] = useState(false);
  const [programarOpen, setProgramarOpen] = useState(false);
  const debounceRef = useRef();
  const [refrescando, setRefrescando] = useState(false);

  // PROMPT 67: `silencioso` = refresco de tiempo real. NO toca `loading` (que desmonta todo
  // el contenido) y no muestra toast si falla: deja en pantalla los datos que ya estaban.
  const load = useCallback(async ({ silencioso = false } = {}) => {
    if (silencioso) setRefrescando(true);
    else setLoading(true);
    try {
      if (vista === "dia") {
        const { data } = await api.get("/agenda/dia", { params: { fecha } });
        setDia(data);
      } else {
        const { data } = await api.get("/agenda/semana", { params: { desde: startOfWeekStr(fecha) } });
        setSemana(data);
      }
    } catch (e) {
      if (silencioso) console.warn("Refresco de agenda en segundo plano falló:", e?.message || e);
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      if (silencioso) setRefrescando(false);
      else setLoading(false);
    }
  }, [vista, fecha]);

  useEffect(() => {
    api.get("/tours", { params: { activo: true } }).then(({ data }) => setTours(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Tiempo real: refresca EN SEGUNDO PLANO y solo si el aviso es de la fecha que se está
  // mirando (día) o de la semana en pantalla. El resto se descarta en el cliente.
  const esDeLaVista = useCallback((msgFecha) => {
    if (!msgFecha) return true; // aviso sin fecha: refrescar por las dudas
    if (vista === "dia") return msgFecha === fecha;
    const desde = startOfWeekStr(fecha);
    return msgFecha >= desde && msgFecha <= addDaysStr(desde, 6);
  }, [vista, fecha]);

  useAgendaSocket((msg) => {
    if (msg.tipo !== "agenda_update") return;
    if (!esDeLaVista(msg.fecha)) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load({ silencioso: true }), REFRESH_DEBOUNCE_MS);
  });

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Refresco sin spinner para las acciones de los diálogos/paneles (el contenido no se desmonta).
  const recargarSilencioso = useCallback(() => load({ silencioso: true }), [load]);

  const openDetail = async (s) => {
    if (s.programada === false) {
      try {
        const { data } = await api.post("/salidas", {
          tour_id: s.tour_id,
          fecha: s.fecha,
          hora: s.hora,
          modalidad: s.modalidad,
          vehiculo: s.vehiculo,
          idioma: s.idioma,
        });
        toast.success("Horario programado. Ya podés cargarle los vehículos del día.");
        load({ silencioso: true });
        setSelSalidaId(data.id);
        setDetailOpen(true);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
      return;
    }
    if (s.es_grupo) {
      setSelGrupoId(s.grupo_id);
      setGrupoDetailOpen(true);
      return;
    }
    setSelSalidaId(s.id);
    setDetailOpen(true);
  };

  const navega = (delta) => setFecha((f) => addDaysStr(f, vista === "dia" ? delta : delta * 7));

  const resumen = dia?.resumen;
  const itemsDia = dia ? [...(dia.regulares || []), ...(dia.privados || [])] : [];

  return (
    <div className="space-y-5" data-testid="agenda-page">
      {/* Barra superior: navegador de fecha + vista + acciones */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navega(-1)} data-testid="agenda-prev" className="h-9 w-9">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2 min-w-[230px] justify-start" data-testid="agenda-date-trigger">
                <CalendarDays className="h-4 w-4 text-slate-400" />
                <span className="capitalize">{formatFechaLarga(fecha)}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={strToDate(fecha)}
                onSelect={(d) => d && setFecha(dateToStr(d))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="icon" onClick={() => navega(1)} data-testid="agenda-next" className="h-9 w-9">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setFecha(todayStr())} data-testid="agenda-hoy" className="text-slate-600">
            Hoy
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium mr-1" data-testid="agenda-live">
            <Radio className="h-3.5 w-3.5 animate-pulse" /> En vivo
            <Loader2
              className={`h-3 w-3 animate-spin text-slate-400 transition-opacity ${refrescando ? "opacity-100" : "opacity-0"}`}
              data-testid="agenda-refrescando"
            />
          </span>
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-white">
            <button
              onClick={() => setVista("dia")}
              data-testid="agenda-vista-dia"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                vista === "dia" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <CalendarDays className="h-4 w-4" /> Día
            </button>
            <button
              onClick={() => setVista("semana")}
              data-testid="agenda-vista-semana"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                vista === "semana" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <CalendarRange className="h-4 w-4" /> Semana
            </button>
          </div>
          {vista === "dia" && (
            <>
              <Button variant="outline" size="sm" onClick={() => setProgramarOpen(true)} className="gap-1.5" data-testid="agenda-programar-btn">
                <CalendarPlus className="h-4 w-4" /> Programar salida
              </Button>
              <Button variant="outline" size="sm" onClick={() => setUnifOpen(true)} className="gap-1.5" data-testid="agenda-unificar-btn">
                <Merge className="h-4 w-4" /> Unificar horarios
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCombinarOpen(true)} className="gap-1.5" data-testid="agenda-combinar-btn">
                <Layers className="h-4 w-4" /> Combinar cupo
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-60">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : vista === "dia" ? (
        <>
          {/* Resumen del día */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            <StatChip icon={Bus} label="Salidas" value={resumen?.salidas ?? 0} />
            <StatChip icon={Users} label="Pasajeros" value={resumen?.pasajeros ?? 0} />
            <StatChip icon={Baby} label="Infantes" value={resumen?.infantes ?? 0} />
            <StatChip icon={TriangleAlert} label="Casi llenas" value={resumen?.casi_llenas ?? 0} tone="amber" />
            <StatChip icon={AlertTriangle} label="Llenas" value={resumen?.llenas ?? 0} tone="red" />
            <StatChip icon={BadgeDollarSign} label="Con saldo" value={resumen?.saldo_pendiente ?? 0} tone="rose" />
          </div>

          {itemsDia.length === 0 && (dia?.sin_programar || []).length === 0 ? (
            <Card className="p-12 text-center text-slate-400" data-testid="agenda-empty">
              <CalendarDays className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No hay salidas con reservas para este día.</p>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Regulares */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Bus className="h-4 w-4 text-slate-500" />
                  <h3 className="font-heading font-semibold text-slate-900">Salidas regulares</h3>
                  <span className="text-xs text-slate-400">({dia.regulares.length})</span>
                </div>
                {dia.regulares.length === 0 ? (
                  <p className="text-sm text-slate-400">Sin salidas regulares hoy.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {dia.regulares.map((s) => (
                      <SalidaCard key={s.id} s={s} onClick={openDetail} />
                    ))}
                  </div>
                )}
              </section>

              {/* Privados */}
              {dia.privados.length > 0 && (
                <section data-testid="agenda-privados-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="h-4 w-4 text-amber-500" />
                    <h3 className="font-heading font-semibold text-slate-900">Servicios privados</h3>
                    <span className="text-xs text-slate-400">({dia.privados.length})</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {dia.privados.map((s) => (
                      <SalidaCard key={s.id} s={s} onClick={openDetail} />
                    ))}
                  </div>
                </section>
              )}

              {/* Horarios del catálogo sin salida creada todavía */}
              {(dia.sin_programar || []).length > 0 && (
                <section data-testid="agenda-sin-programar-section">
                  <div className="flex items-center gap-2 mb-3">
                    <CalendarPlus className="h-4 w-4 text-slate-400" />
                    <h3 className="font-heading font-semibold text-slate-900">Horarios sin programar</h3>
                    <span className="text-xs text-slate-400">({dia.sin_programar.length})</span>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">
                    Horarios configurados en el catálogo del tour para este día que todavía no tienen
                    reservas ni salida armada. Hacé clic para programarlos de antemano.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {dia.sin_programar.map((s) => (
                      <SalidaCard key={s.id} s={s} onClick={openDetail} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </>
      ) : (
        <SemanaView dias={semana?.dias || []} onSelect={openDetail} />
      )}

      <SalidaDetailSheet
        open={detailOpen}
        onOpenChange={setDetailOpen}
        salidaId={selSalidaId}
        tours={tours}
        onChanged={recargarSilencioso}
      />

      <GrupoDetailSheet
        open={grupoDetailOpen}
        onOpenChange={setGrupoDetailOpen}
        grupoId={selGrupoId}
        tours={tours}
        onChanged={recargarSilencioso}
      />

      <UnificarDialog open={unifOpen} onOpenChange={setUnifOpen} fecha={fecha} items={itemsDia} onDone={recargarSilencioso} />

      <CombinarHorarioDialog open={combinarOpen} onOpenChange={setCombinarOpen} fecha={fecha} items={itemsDia} onDone={recargarSilencioso} />

      <ProgramarSalidaDialog
        open={programarOpen}
        onOpenChange={setProgramarOpen}
        fecha={fecha}
        tours={tours}
        onCreated={(salida) => {
          recargarSilencioso();
          setSelSalidaId(salida.id);
          setDetailOpen(true);
        }}
      />
    </div>
  );
}
